import { StrictMode, startTransition } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App";
import { CartProvider } from "./features/cart";
// Rechtstreeks uit ./auth, NIET uit de features/account-barrel: die barrel exporteert ook alle
// (lazy-loaded) accountpagina's, en een statische import van ook maar één barrel-export dwingt
// Rollup het hele bestand — incl. die accountpagina's en hún firebase/functions-imports — in de
// kritieke eerste-lading-bundel te zetten. Zie ook cart/index.ts hieronder voor hetzelfde risico.
import { CustomerAuthProvider } from "./features/account/auth";
import { loadCatalogData } from "@/lib/catalog";
import { basenameFromPath } from "@/lib/locale-url";
import { reloadOnceForChunkError } from "./components/RouteErrorBoundary";
import { bewaarEerstePaint } from "./lib/first-paint";
import "./index.css";

// Nieuwe deploy → oude route-chunk 404 bij navigeren (bv. na inactiviteit met een oude tab).
// Vite meldt dit via `vite:preloadError`; dan herladen we één keer i.p.v. een wit scherm.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  reloadOnceForChunkError();
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found");

// Dode portals uit een statische snapshot opruimen.
//
// Een portal (het "Laten plaatsen?"-randje, modals) hangt ónder <body>, buiten #root. Stond zo'n
// element in de voorgerenderde HTML, dan blijft het na het opstarten staan: React hydrateert alleen
// #root en raakt de rest nooit meer aan. Het gevolg is een exemplaar dat je niet kúnt wegklikken,
// naast het echte dat de app er zelf naast zet. De build laat ze er sinds 26-07-2026 niet meer in
// (zie scripts/prerender.mjs), maar pagina's die al bij bezoekers in de cache staan wél — vandaar
// dat we hier vóór de eerste render alles opruimen wat niet van React of van het document is.
for (const el of Array.from(document.body.children)) {
  if (el === rootEl || ["SCRIPT", "NOSCRIPT", "TEMPLATE", "STYLE", "LINK"].includes(el.tagName)) continue;
  el.remove();
}

// De al zichtbare pagina bewaren vóórdat createRoot 'm leegt — anders zie je hem verdwijnen en
// meteen weer terugkomen. Zie lib/first-paint.
bewaarEerstePaint(rootEl.innerHTML, window.location.pathname);

// Eerst de catalogus-seed (async chunk, ~1,4 MB — zie lib/catalog/data.ts), dán pas renderen:
// zo valt het parsen van die data niet in dezelfde lange opstart-taak als de app-code, en de
// voorgerenderde HTML blijft gewoon zichtbaar tot de render commit. Mislukt de chunk (nieuwe
// deploy, flaky netwerk), dan vangt de vite:preloadError-handler hierboven 'm op met één
// herlaad; de .catch is de laatste vangrail zodat de site óók zonder catalogus nog opstart.
//
// De render zelf als transition: React 19 knipt 'm dan in kleine, onderbreekbare stukjes
// (time-slicing) i.p.v. één blokkerende taak van honderden ms — gemeten als de grootste
// Total Blocking Time-post. De bezoeker ziet geen verschil.
const root = createRoot(rootEl);
loadCatalogData()
  .catch((err) => console.warn("[catalog] seed laden mislukt — site start zonder catalogus", err))
  .then(() => {
    startTransition(() => {
      root.render(
        <StrictMode>
          {/* basename "/en" op de Engelse site: de hele routetabel en álle <Link>s liften mee zonder
          dat er ook maar één route gedupliceerd hoeft te worden. Nederlandse paden blijven exact
          zoals ze waren (basename "/"). Zie lib/locale-url.ts. */}
      <BrowserRouter basename={basenameFromPath(window.location.pathname)}>
            <CustomerAuthProvider>
              <CartProvider>
                <App />
              </CartProvider>
            </CustomerAuthProvider>
          </BrowserRouter>
        </StrictMode>,
      );
    });
  });

// Route-chunks vast opwarmen zodra iemand er ECHT waarschijnlijk naartoe gaat — bij hover/focus
// (desktop) of touchstart (mobiel), niet blind voor iedereen.
//
// Stond hier eerder als "warm de 5 populairste routes op zodra de browser idle is". Dat trok elke
// route (én hún eigen lazy sub-onderdelen: bestelbon, roosterupsell, leverregio's, ...) samen tot
// een paar MB extra JS die praktisch elke bezoeker alsnog kreeg, ook wie alleen de homepage scande
// en meteen wegging — Lighthouse's netwerkafhankelijkheidsstructuur liet die hele keten zien, ruim
// binnen het venster waarin FCP/LCP/TBT gemeten worden. Hover/touchstart gaat aan een klik vooraf
// (net als de bekende "quicklink"-techniek), dus de chunk begint te laden vlak vóór je 'm nodig
// hebt — niet voor iedereen die de pagina alleen bekijkt.
const ROUTE_PREFETCH: { test: RegExp; load: () => Promise<unknown> }[] = [
  { test: /^\/glas-op-maat\//, load: () => import("./features/configurator/ConfiguratorRoute") },
  { test: /^\/categorie\//, load: () => import("./features/category/CategoryPage") },
  { test: /^\/artikel\//, load: () => import("./features/product/ProductPage") },
  { test: /^\/glas-op-maat(\?|$)/, load: () => import("./features/overview/GlasOverview") },
  { test: /^\/glassoorten(\?|$)/, load: () => import("./features/glas/GlassoortenPage") },
  { test: /^\/winkelwagen(\?|$)/, load: () => import("./features/cart") },
];

function setupIntentPrefetch(): void {
  const done = new Set<() => Promise<unknown>>();
  const onIntent = (e: Event): void => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const a = target.closest("a[href]");
    const href = a?.getAttribute("href");
    if (!href) return;
    for (const r of ROUTE_PREFETCH) {
      if (!done.has(r.load) && r.test.test(href)) { done.add(r.load); void r.load(); }
    }
  };
  // mouseover (bubbelt, i.t.t. mouseenter) voor hover-intentie; touchstart voor mobiel vóór de tap
  // z'n click-event vuurt; focusin voor toetsenbordnavigatie. Alle drie passief en gedelegeerd op
  // document, dus geen listener per link nodig.
  document.addEventListener("mouseover", onIntent, { passive: true });
  document.addEventListener("touchstart", onIntent, { passive: true });
  document.addEventListener("focusin", onIntent, { passive: true });
}
setupIntentPrefetch();
